import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { ProductCard } from '../components/ProductCard';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';

// Detect if we're on Emergent preview (use backend) or elsewhere like Netlify (use local /api)
const isEmergentPreview = typeof window !== 'undefined' && window.location.hostname.includes('emergentagent.com');
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Only use backend URL on Emergent preview, otherwise use relative /api path for Netlify Functions
const API = (isEmergentPreview && BACKEND_URL) ? `${BACKEND_URL}/api` : '/api';

export const Shop = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${API}/products`);
        setProducts(response.data.products || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Unable to load products. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  return (
    <>
      <Helmet>
        <title>Shop | Wild Ones LLC</title>
        <meta
          name="description"
          content="Browse our collection of premium southern-style clothing. Quality long sleeve tees featuring unique outdoor designs for hunters, fishers, and nature lovers."
        />
        <link rel="canonical" href="https://wildones.com/shop" />
      </Helmet>

      <main className="pt-[116px]" data-testid="shop-page">
        {/* Page Header */}
        <section className="bg-wild-khaki/30 py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <h1
              className="font-headings text-5xl md:text-6xl lg:text-7xl tracking-wider text-wild-dark mb-4"
              data-testid="shop-title"
            >
              SHOP ALL
            </h1>
            <p className="font-subheadings text-lg md:text-xl text-wild-stone italic max-w-2xl mx-auto">
              Premium southern-style apparel for the wild at heart
            </p>
          </div>
        </section>

        {/* Products Section */}
        <section className="py-16 md:py-24 bg-wild-sand">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            {isLoading ? (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12"
                data-testid="shop-loading"
              >
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="space-y-4">
                    <Skeleton className="aspect-square w-full" />
                    <Skeleton className="h-4 w-3/4 mx-auto" />
                    <Skeleton className="h-4 w-1/2 mx-auto" />
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="text-center py-12" data-testid="shop-error">
                <p className="font-body text-wild-stone mb-4">{error}</p>
                <Button
                  onClick={() => window.location.reload()}
                  className="bg-wild-leather text-white hover:bg-wild-leather/90"
                >
                  Try Again
                </Button>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12" data-testid="shop-empty">
                <p className="font-headings text-2xl text-wild-stone mb-2">
                  NO PRODUCTS YET
                </p>
                <p className="font-body text-wild-stone">
                  More coming every month — check back soon!
                </p>
              </div>
            ) : (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12"
                data-testid="shop-products-grid"
              >
                {products.map((product, index) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    featured={index < 2}
                  />
                ))}
              </div>
            )}

            {/* Coming Soon Note */}
            {!isLoading && !error && products.length > 0 && (
              <div className="text-center mt-16 py-8 border-t border-wild-khaki">
                <p className="font-subheadings text-lg text-wild-stone italic">
                  More coming every month — stay wild!
                </p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
};
